import { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import styled from "styled-components";

import SntCheckBox from "@cleanup/components/SntCheckBox/SntCheckBox";
import SntRadio from "@cleanup/components/SntRadio/SntRadio";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";

interface SearchField {
  _idx: string;
  key: string;
  label?: string;
  description?: string;
  filterType: string;
  searchType?: string;
  filterTypeSettings?: Record<string, unknown>;
  categoryId?: string;
  [key: string]: unknown;
}

interface FilterParam {
  filterKey: string;
  filterType: string;
  categoryId?: string;
  searchType?: string;
  filterValue?: Record<string, unknown>;
  notFilter?: boolean;
  [key: string]: unknown;
}

interface BaseProps {
  descriptor: SearchField;
  data: FilterParam;
  onChange: (next: FilterParam | { idx: string; data: null }) => void;
  disabled?: boolean;
}

const InlineStyle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  .row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    align-items: center;
  }

  .label {
    color: #424b59;
    font-size: 0.85rem;
    margin-bottom: 0;
    min-width: 70px;
  }

  .input-narrow {
    max-width: 200px;
  }

  .not-toggle {
    margin-top: 0.4rem;
  }
`;

const emitChange = (
  base: FilterParam,
  filterValue: Record<string, unknown> | undefined,
  notFilter: boolean | undefined,
  onChange: BaseProps["onChange"]
) => {
  const next: FilterParam = { ...base };
  if (filterValue && Object.keys(filterValue).length > 0) {
    next.filterValue = filterValue;
  } else {
    delete next.filterValue;
  }
  if (notFilter !== undefined) next.notFilter = notFilter;
  onChange(next);
};

const NotToggle: React.FC<{
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}> = ({ checked, disabled, onChange }) => (
  <div className="not-toggle">
    <SntCheckBox
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      label={translate(LanguageKey.exclude_not_key)}
    />
  </div>
);

export const InlineStringFilter: React.FC<BaseProps> = ({
  descriptor,
  data,
  onChange,
  disabled,
}) => {
  const fts = (descriptor.filterTypeSettings || {}) as {
    notDefinedOption?: { optionLabel?: string };
    definedOption?: { optionLabel?: string };
    searchFieldPlaceholder?: string;
  };

  const initial = useMemo(() => {
    const fv = (data.filterValue || {}) as Record<string, unknown>;
    return {
      searchText: (fv.searchText as string) || "",
      includeEmpty: Boolean(fv.includeEmpty),
      includeNoEmpty: Boolean(fv.includeNoEmpty),
      notFilter: Boolean(data.notFilter),
    };
  }, [data]);

  const [state, setState] = useState(initial);
  useEffect(() => setState(initial), [initial]);

  const propagate = (s: typeof state) => {
    const fv: Record<string, unknown> = {};
    if (s.includeEmpty) fv.includeEmpty = true;
    else if (s.includeNoEmpty) fv.includeNoEmpty = true;
    else if (s.searchText.trim() !== "") fv.searchText = s.searchText;
    emitChange(data, fv, s.notFilter, onChange);
  };

  const update = (patch: Partial<typeof state>) => {
    const next = { ...state, ...patch };
    setState(next);
    propagate(next);
  };

  const textDisabled = state.includeEmpty || state.includeNoEmpty;

  return (
    <InlineStyle>
      <Form.Control
        type="text"
        placeholder={fts.searchFieldPlaceholder || ""}
        value={state.searchText}
        disabled={disabled || textDisabled}
        onChange={(e) => update({ searchText: e.target.value })}
      />
      {fts.notDefinedOption && (
        <SntCheckBox
          checked={state.includeEmpty}
          disabled={disabled}
          onChange={(v) =>
            update({
              includeEmpty: !state.includeEmpty && v,
              includeNoEmpty: state.includeNoEmpty && !v,
            })
          }
          label={fts.notDefinedOption.optionLabel || "Include empty"}
        />
      )}
      {fts.definedOption && (
        <SntCheckBox
          checked={state.includeNoEmpty}
          disabled={disabled}
          onChange={(v) =>
            update({
              includeNoEmpty: !state.includeNoEmpty && v,
              includeEmpty: state.includeEmpty && !v,
            })
          }
          label={fts.definedOption.optionLabel || "Include defined"}
        />
      )}
      <NotToggle
        checked={state.notFilter}
        disabled={disabled || textDisabled}
        onChange={(v) => update({ notFilter: v })}
      />
    </InlineStyle>
  );
};

export const InlineMultiStringFilter: React.FC<BaseProps> = ({
  descriptor,
  data,
  onChange,
  disabled,
}) => {
  const fts = (descriptor.filterTypeSettings || {}) as {
    searchFieldPlaceholder?: string;
    numInputFieldLines?: number;
  };

  const initial = useMemo(() => {
    const fv = (data.filterValue || {}) as Record<string, unknown>;
    return {
      searchText: (fv.searchText as string) || "",
    };
  }, [data]);

  const [state, setState] = useState(initial);
  useEffect(() => setState(initial), [initial]);

  const propagate = (s: typeof state) => {
    const fv: Record<string, unknown> = {};
    if (s.searchText.trim() !== "") fv.searchText = s.searchText;
    emitChange(data, fv, undefined, onChange);
  };
  const update = (patch: Partial<typeof state>) => {
    const next = { ...state, ...patch };
    setState(next);
    propagate(next);
  };

  return (
    <InlineStyle>
      <Form.Control
        as="textarea"
        rows={fts.numInputFieldLines || 4}
        placeholder={fts.searchFieldPlaceholder || "One value per line"}
        value={state.searchText}
        disabled={disabled}
        style={{ resize: "none" }}
        onChange={(e) => update({ searchText: e.target.value })}
      />
    </InlineStyle>
  );
};

const NUMERIC_OPS = [
  { value: "EQ", label: "= equal to" },
  { value: "GT", label: "> greater than" },
  { value: "LT", label: "< less than" },
  { value: "BETWEEN", label: "between" },
  { value: "NULL", label: "is empty" },
  { value: "NOT_NULL", label: "is not empty" },
];

export const InlineNumericFilter: React.FC<BaseProps> = ({
  data,
  onChange,
  disabled,
}) => {
  const initial = useMemo(() => {
    const fv = (data.filterValue || {}) as Record<string, unknown>;
    return {
      operator: (fv.operator as string) || "EQ",
      eq: fv.eq != null ? String(fv.eq) : "",
      gt: fv.gt != null ? String(fv.gt) : "",
      lt: fv.lt != null ? String(fv.lt) : "",
      notFilter: Boolean(data.notFilter),
    };
  }, [data]);

  const [state, setState] = useState(initial);
  const localChangeRef = useRef(false);
  useEffect(() => {
    if (localChangeRef.current) {
      localChangeRef.current = false;
      return;
    }
    setState(initial);
  }, [initial]);

  const propagate = (s: typeof state) => {
    const fv: Record<string, unknown> = { operator: s.operator };
    if (s.operator === "EQ" && s.eq !== "") fv.eq = Number(s.eq);
    else if (s.operator === "GT" && s.gt !== "") fv.gt = Number(s.gt);
    else if (s.operator === "LT" && s.lt !== "") fv.lt = Number(s.lt);
    else if (s.operator === "BETWEEN") {
      if (s.gt !== "") fv.gt = Number(s.gt);
      if (s.lt !== "") fv.lt = Number(s.lt);
    } else if (s.operator !== "NULL" && s.operator !== "NOT_NULL") {
      delete fv.operator;
    }
    const hasValue =
      s.operator === "NULL" ||
      s.operator === "NOT_NULL" ||
      (s.operator === "EQ" && s.eq !== "") ||
      (s.operator === "GT" && s.gt !== "") ||
      (s.operator === "LT" && s.lt !== "") ||
      (s.operator === "BETWEEN" && (s.gt !== "" || s.lt !== ""));
    emitChange(data, hasValue ? fv : undefined, s.notFilter, onChange);
  };
  const update = (patch: Partial<typeof state>) => {
    const next = { ...state, ...patch };
    setState(next);
    localChangeRef.current = true;
    propagate(next);
  };

  return (
    <InlineStyle>
      <div className="row">
        <Form.Select
          className="input-narrow"
          value={state.operator}
          disabled={disabled}
          onChange={(e) => update({ operator: e.target.value })}
        >
          {NUMERIC_OPS.map((op) => (
            <option key={op.value} value={op.value}>
              {op.label}
            </option>
          ))}
        </Form.Select>

        {state.operator === "EQ" && (
          <Form.Control
            type="number"
            className="input-narrow"
            value={state.eq}
            disabled={disabled}
            onChange={(e) => update({ eq: e.target.value })}
          />
        )}
        {state.operator === "GT" && (
          <Form.Control
            type="number"
            className="input-narrow"
            value={state.gt}
            disabled={disabled}
            onChange={(e) => update({ gt: e.target.value })}
          />
        )}
        {state.operator === "LT" && (
          <Form.Control
            type="number"
            className="input-narrow"
            value={state.lt}
            disabled={disabled}
            onChange={(e) => update({ lt: e.target.value })}
          />
        )}
        {state.operator === "BETWEEN" && (
          <>
            <Form.Control
              type="number"
              className="input-narrow"
              placeholder="min"
              value={state.gt}
              disabled={disabled}
              onChange={(e) => update({ gt: e.target.value })}
            />
            <span>and</span>
            <Form.Control
              type="number"
              className="input-narrow"
              placeholder="max"
              value={state.lt}
              disabled={disabled}
              onChange={(e) => update({ lt: e.target.value })}
            />
          </>
        )}
      </div>
      <NotToggle
        checked={state.notFilter}
        disabled={disabled}
        onChange={(v) => update({ notFilter: v })}
      />
    </InlineStyle>
  );
};

export const InlineBooleanFilter: React.FC<BaseProps> = ({
  data,
  onChange,
  disabled,
}) => {
  const initial = useMemo(() => {
    const fv = (data.filterValue || {}) as Record<string, unknown>;
    let mode: "yes" | "no" | "na" | "none" = "none";
    if (fv.naOnly) mode = "na";
    else if (fv.value === true) mode = "yes";
    else if (fv.value === false) mode = "no";
    return { mode, notFilter: Boolean(data.notFilter) };
  }, [data]);

  const [state, setState] = useState(initial);
  useEffect(() => setState(initial), [initial]);

  const propagate = (s: typeof state) => {
    let fv: Record<string, unknown> | undefined;
    if (s.mode === "yes") fv = { value: true, naOnly: false };
    else if (s.mode === "no") fv = { value: false, naOnly: false };
    else if (s.mode === "na") fv = { value: null, naOnly: true };
    emitChange(data, fv, s.notFilter, onChange);
  };
  const update = (patch: Partial<typeof state>) => {
    const next = { ...state, ...patch };
    setState(next);
    propagate(next);
  };

  return (
    <InlineStyle>
      <div className="row">
        {(["yes", "no", "na"] as const).map((m) => (
          <SntRadio
            key={m}
            value={m}
            selectedValue={state.mode}
            disabled={disabled}
            onChange={() => update({ mode: m })}
            label={m === "yes" ? "Yes" : m === "no" ? "No" : "N/A"}
          />
        ))}
      </div>
      <NotToggle
        checked={state.notFilter}
        disabled={disabled || state.mode === "na"}
        onChange={(v) => update({ notFilter: v })}
      />
    </InlineStyle>
  );
};

